import React, { useState, useEffect, useMemo } from 'react'
import { useDataQuery, useDataEngine } from '@dhis2/app-runtime'
import { Card, LinearLoader, Button, AlertBar } from '@dhis2/ui'
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts'
import { CONFIG } from '../../leadership/api/LeadershipServices';
import { useFiscalYear } from '../../../shared/FiscalYearContext';
import { isWithinFiscalYear, isFiscalYearActive, getFiscalYearLabel } from '../../GoalsManagement/utils/fiscalYearHelpers';

const useMediaQuery = (query) => {
  const [matches, setMatches] = React.useState(
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener ? mql.addEventListener('change', handler) : mql.addListener(handler);
    return () => {
      mql.removeEventListener ? mql.removeEventListener('change', handler) : mql.removeListener(handler);
    };
  }, [query]);

  return matches;
};

const stripHtml = (html) => {
  if (!html) return '';
  // Use a detached element so we can decode entities too
  const tmp = document.createElement('div');
  tmp.innerHTML = html;

  // Insert a space between block-level elements so words don't run together
  tmp.querySelectorAll('div, br, p, li').forEach(el => {
    el.insertAdjacentHTML('beforebegin', ' ');
  });

  const text = tmp.textContent || tmp.innerText || '';
  return text.replace(/\s+/g, ' ').trim();
};


const LeadershipDashboard = ({ orgUnitId, accessibleOrgUnitIds = [], filterScope = 'all', 
                               performanceData: externalPerformanceData, }) => {
  const [selectedStaff, setSelectedStaff] = useState(null)
  const [competencies, setCompetencies] = useState([])
  const [competencyDefinitions, setCompetencyDefinitions] = useState({})
  const [viewMode, setViewMode] = useState('aggregate')
  const [loadingCompetencies, setLoadingCompetencies] = useState(true) 
  const [processedData, setProcessedData] = useState([]) 
  const [processing, setProcessing] = useState(false) 
  const [rawEvents, setRawEvents] = useState([])
  
  const [userAttributeId, setUserAttributeId] = useState(null)

  const { fiscalYear } = useFiscalYear();

  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');

  const engine = useDataEngine()
  const [ratingScale, setRatingScale] = useState({
    1: { label: '1 - Unsatisfactory', color: '#dc3545', description: 'Rarely meets expectations' },
    2: { label: '2 - Developing', color: '#ff9800', description: 'Sometimes meets expectations; improvement needed' },
    3: { label: '3 - Competent', color: '#ffc107', description: 'Meets expectations consistently' },
    4: { label: '4 - Strong Performer', color: '#4caf50', description: 'Frequently exceeds expectations' },
    5: { label: '5 - Role Model', color: '#28a745', description: 'Consistently exceeds expectations; sets example for others' }
  })

  const POSITION_ATTRIBUTES = {
    USER_ID: 'V66IFsw5kq6',      // Attribute that stores the user ID
    TITLE: 'NWe61Sa41CS',        // Attribute that stores the position title
    DEPARTMENT: 'YsGPrrVLAuM',    // Attribute that stores the department
    LEVEL: 'gp4AnggIDi8',        // Attribute that stores the level
    REPORTS_TO: 'SjP47qi3JeB',   // Attribute that stores reports to reference
    STATUS: 'Ch7TyL91heU',       // Attribute that stores status
    EMPLOYEE_ID: 'VwhhrV1ODQK'   // Attribute that stores employee ID
  };



  // Helper function for default colors
  const getDefaultColor = (rating) => {
    const colors = {
      1: '#dc3545',
      2: '#ff9800', 
      3: '#ffc107',
      4: '#4caf50',
      5: '#28a745'
    }
    return colors[rating] || '#6c757d'
  }

  // Progress Bar Component
  const ProgressBar = ({ value, height = '6px', borderRadius = '3px', color = '#007bff', showLabel = false }) => (
    <div style={{ width: '100%' }}>
      <div style={{
        width: '100%',
        height,
        backgroundColor: '#e5e7eb',
        borderRadius
      }}>
        <div style={{
          width: `${Math.min(Math.max(value || 0, 0), 100)}%`,
          height: '100%',
          backgroundColor: color,
          borderRadius,
          transition: 'width 0.3s ease'
        }} />
      </div>
      {showLabel && (
        <div style={{ fontSize: '10px', marginTop: '2px', textAlign: 'center' }}>
          {value.toFixed(0)}%
        </div>
      )}
    </div>
  )

  const loadCompetencyConfig = async () => {
    if (!orgUnitId) return
    
    setLoadingCompetencies(true)
    try {
      const query = {
        configEvents: {
          resource: 'events',
          params: {
            program: CONFIG.program,
            programStage: CONFIG.stages.config,
            orgUnit: orgUnitId,
            ouMode: 'ACCESSIBLE',
            fields: 'event,dataValues[dataElement,value]',
            pageSize: 50,
          },
        },
      }
      
      const result = await engine.query(query)
      const events = result.configEvents?.events || []
      
      const loadedCompetencies = []
      const definitions = {}
      
      events.forEach(event => {
        const codeDV = event.dataValues.find(dv => dv.dataElement === CONFIG.de.competencyCode)
        const defDV = event.dataValues.find(dv => dv.dataElement === CONFIG.de.competencyDefinition)
        const weightDV = event.dataValues.find(dv => dv.dataElement === CONFIG.de.weightValue)
        const sortOrderDV = event.dataValues.find(dv => dv.dataElement === CONFIG.de.sortOrder)
        
        if (codeDV?.value) {
          const competencyId = event.event
          const competencyName = codeDV.value
          loadedCompetencies.push({
            competencyId,
            name: competencyName,
            definition: stripHtml(defDV?.value || ''),
            weight: weightDV ? parseFloat(weightDV.value) : 0.25,
            sortOrder: sortOrderDV !== undefined ? parseFloat(sortOrderDV.value) : null,
          })
          
          definitions[competencyName] = defDV?.value
            ? stripHtml(defDV.value)
            : `${competencyName} competency`

        }
      })

      loadedCompetencies.sort((a, b) => {
        if (a.sortOrder == null && b.sortOrder == null) return 0
        if (a.sortOrder == null) return 1
        if (b.sortOrder == null) return -1
        return a.sortOrder - b.sortOrder
      })
      
      if (loadedCompetencies.length === 0) {
        const defaultCompetencies = [
          { competencyId: 'respect', name: 'RESPECT', definition: 'Treating others with dignity and consideration', weight: 0.25 },
          { competencyId: 'excellence', name: 'EXCELLENCE', definition: 'Striving for highest quality in all deliverables', weight: 0.25 },
          { competencyId: 'learning', name: 'LEARNING', definition: 'Continuous improvement and skill development', weight: 0.25 },
          { competencyId: 'integrity', name: 'INTEGRITY', definition: 'Honest and ethical behavior', weight: 0.25 },
        ]
        setCompetencies(defaultCompetencies)
        defaultCompetencies.forEach(comp => {
          definitions[comp.name] = comp.definition
        })
      } else {
        setCompetencies(loadedCompetencies)
      }
      
      setCompetencyDefinitions(definitions)
      
    } catch (error) {
      console.error('Failed to load competency config:', error)
      const defaultCompetencies = [
        { competencyId: 'respect', name: 'RESPECT', definition: 'Treating others with dignity and consideration', weight: 0.25 },
        { competencyId: 'excellence', name: 'EXCELLENCE', definition: 'Striving for highest quality in all deliverables', weight: 0.25 },
        { competencyId: 'learning', name: 'LEARNING', definition: 'Continuous improvement and skill development', weight: 0.25 },
        { competencyId: 'integrity', name: 'INTEGRITY', definition: 'Honest and ethical behavior', weight: 0.25 },
      ]
      setCompetencies(defaultCompetencies)
      const definitions = {}
      defaultCompetencies.forEach(comp => {
        definitions[comp.name] = comp.definition
      })
      setCompetencyDefinitions(definitions)
    } finally {
      setLoadingCompetencies(false)
    }
  }

  const RATING_SCALE_NAMESPACE = 'leadership-rating-scale';
  const getDataStoreKey = (orgId) => orgId ? `scale_${orgId}` : 'scale_global';
  
  const getParentOrgUnitId = async (orgUnitId) => {
    if (!orgUnitId) return null;
    try {
      const response = await engine.query({
        orgUnit: {
          resource: `organisationUnits/${orgUnitId}`,
          params: { fields: 'path,parent[id]' }
        }
      });
      const path = response.orgUnit?.path;
      if (path) {
        const pathParts = path.split('/').filter(Boolean);
        if (pathParts.length >= 5) return pathParts[4];
        return response.orgUnit?.parent?.id || null;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const loadRatingScaleConfig = async () => {
  if (!orgUnitId) return;
  try {
    const parentOrgId = await getParentOrgUnitId(orgUnitId);
    const orgsToCheck = [orgUnitId];
    if (parentOrgId && parentOrgId !== orgUnitId) orgsToCheck.push(parentOrgId);

    let loaded = null;
    for (const orgId of orgsToCheck) {
      const key = getDataStoreKey(orgId);
      try {
        const response = await engine.query({
          ratingScale: {
            resource: `dataStore/${RATING_SCALE_NAMESPACE}/${key}`,
          }
        });
        const data = response.ratingScale;
        
        // FIX: Handle both array and object formats
        if (data) {
          if (Array.isArray(data) && data.length > 0) {
            loaded = data;
            break;
          } else if (typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length > 0) {
            // If it's an object, convert to array format for processing
            loaded = Object.entries(data).map(([value, item]) => ({
              value: parseInt(value),
              label: item.label,
              color: item.color,
              description: item.description
            }));
            break;
          }
        }
      } catch (error) {
        // continue to next org
        console.log(`No rating scale found for ${orgId}`);
      }
    }

    if (!loaded) {
      try {
        const globalResponse = await engine.query({
          ratingScale: {
            resource: `dataStore/${RATING_SCALE_NAMESPACE}/scale_global`,
          }
        });
        const globalData = globalResponse.ratingScale;
        if (globalData) {
          if (Array.isArray(globalData) && globalData.length > 0) {
            loaded = globalData;
          } else if (typeof globalData === 'object' && !Array.isArray(globalData) && Object.keys(globalData).length > 0) {
            loaded = Object.entries(globalData).map(([value, item]) => ({
              value: parseInt(value),
              label: item.label,
              color: item.color,
              description: item.description
            }));
          }
        }
      } catch (error) {
        console.log('No global rating scale found, using defaults');
      }
    }

    if (loaded && loaded.length > 0) {
      const newRatingScale = {};
      loaded.forEach((item) => {
        const value = Number(item.value);
        if (!Number.isNaN(value)) {
          newRatingScale[value] = {
            label: item.label || `${value}`,
            color: item.color || getDefaultColor(value),
            description: item.description || ''
          };
        }
      });
      
      // Ensure all ratings 1-5 are present
      for (let i = 1; i <= 5; i++) {
        if (!newRatingScale[i]) {
          newRatingScale[i] = {
            label: getDefaultLabel(i),
            color: getDefaultColor(i),
            description: getDefaultDescription(i)
          };
        }
      }
      
      console.log('Loaded rating scale:', newRatingScale);
      setRatingScale(newRatingScale);
    } else {
      // Use default rating scale with all 5 levels
      const defaultScale = {
        1: { label: '1 - Unsatisfactory', color: '#dc3545', description: 'Rarely meets expectations' },
        2: { label: '2 - Developing', color: '#ff9800', description: 'Sometimes meets expectations; improvement needed' },
        3: { label: '3 - Competent', color: '#ffc107', description: 'Meets expectations consistently' },
        4: { label: '4 - Strong Performer', color: '#4caf50', description: 'Frequently exceeds expectations' },
        5: { label: '5 - Role Model', color: '#28a745', description: 'Consistently exceeds expectations; sets example for others' }
      };
      console.log('Using default rating scale:', defaultScale);
      setRatingScale(defaultScale);
    }
  } catch (error) {
    console.error('Failed to load rating scale config:', error);
    // Set default scale on error
    setRatingScale({
      1: { label: '1 - Unsatisfactory', color: '#dc3545', description: 'Rarely meets expectations' },
      2: { label: '2 - Developing', color: '#ff9800', description: 'Sometimes meets expectations; improvement needed' },
      3: { label: '3 - Competent', color: '#ffc107', description: 'Meets expectations consistently' },
      4: { label: '4 - Strong Performer', color: '#4caf50', description: 'Frequently exceeds expectations' },
      5: { label: '5 - Role Model', color: '#28a745', description: 'Consistently exceeds expectations; sets example for others' }
    });
  }
};

// Helper functions for default values
const getDefaultLabel = (rating) => {
  const labels = {
    1: '1 - Unsatisfactory',
    2: '2 - Developing',
    3: '3 - Competent',
    4: '4 - Strong Performer',
    5: '5 - Role Model'
  };
  return labels[rating] || `${rating}`;
};

const getDefaultDescription = (rating) => {
  const descriptions = {
    1: 'Rarely meets expectations',
    2: 'Sometimes meets expectations; improvement needed',
    3: 'Meets expectations consistently',
    4: 'Frequently exceeds expectations',
    5: 'Consistently exceeds expectations; sets example for others'
  };
  return descriptions[rating] || '';
};

 const fetchAllUsers = async () => {
  try {
    // Fetch users with pagination to get all
    let allUsers = []
    let page = 1
    let totalPages = 1
    
    while (page <= totalPages) {
      const userQuery = {
        users: {
          resource: 'users',
          params: {
            fields: 'id,displayName,username,organisationUnits',
            pageSize: 50,
            page: page
          }
        }
      }
      const result = await engine.query(userQuery)
      const usersData = result.users
      allUsers = [...allUsers, ...(usersData?.users || [])]
      
      totalPages = usersData?.pager?.pageCount || 1
      page++
    }
    
    // Create a map of userId -> user details
    const userMap = new Map()
    allUsers.forEach(user => {
      userMap.set(user.id, {
        id: user.id,
        name: user.displayName,
        username: user.username,
        orgUnits: user.organisationUnits
      })
      // Also map by username (important for looking up by staffId)
      if (user.username) {
        userMap.set(user.username, {
          id: user.id,
          name: user.displayName,
          username: user.username,
          orgUnits: user.organisationUnits
        })
      }
    })
    console.log(`Loaded ${userMap.size} user mappings (${allUsers.length} users)`)
    return userMap
  } catch (error) {
    console.error('Failed to fetch users:', error)
    return new Map()
  }
}

  // Load configs on mount
  useEffect(() => {
    const loadAllConfigs = async () => {
      await loadCompetencyConfig()
      await loadRatingScaleConfig()
    }
    loadAllConfigs()
  }, [orgUnitId, engine])

  const eventsQuery = useMemo(() => ({
    eventsData: {
      resource: 'events',
      params: {
        program: CONFIG.program,
        orgUnit: orgUnitId,
        ouMode: 'DESCENDANTS',
        fields: 'event,programStage,eventDate,orgUnit,orgUnitName,trackedEntityInstance,dataValues[dataElement,value]',
        pageSize: 1000,
        order: 'eventDate:desc'
      }
    }
  }), [orgUnitId])

  const shouldFetchOwnData = !externalPerformanceData;

  const { loading: eventsLoading, error: eventsError, data: eventsData } = useDataQuery(eventsQuery)
  

  useEffect(() => {
    const findUserAttributeId = async () => {
      if (!orgUnitId || userAttributeId) return;
      
      try {
        const sampleQuery = {
          samplePosition: {
            resource: 'trackedEntityInstances',
            params: {
              trackedEntityType: 'UGGo0Ebjl0E',
              ou: orgUnitId,
              ouMode: 'DESCENDANTS',
              fields: 'trackedEntityInstance,attributes[attribute[id,name,code],value]',
              pageSize: 1
            }
          }
        };
        
        const result = await engine.query(sampleQuery);
        const sampleTeiData = result.samplePosition?.trackedEntityInstances?.[0];
        
        if (sampleTeiData && sampleTeiData.attributes) {
          for (const attr of sampleTeiData.attributes) {
            const value = attr.value;
            if (value && value.length === 11 && /^[A-Za-z0-9]+$/.test(value)) {
              const attrId = attr.attribute?.id || attr.attribute;
              if (attrId) {
                setUserAttributeId(attrId);
                console.log(`Cached user attribute ID: ${attrId}`);
                break;
              }
            }
          }
        }
      } catch (error) {
        console.error('Error finding user attribute ID:', error);
      }
    };
    
    findUserAttributeId();
  }, [orgUnitId, engine, userAttributeId]);

  // Process data when all dependencies are loaded
    useEffect(() => {
      const processData = async () => {

        if (eventsLoading || loadingCompetencies || !fiscalYear?.start || !fiscalYear?.end) {
          console.log('⏳ Waiting for fiscal year or data to load...', { fiscalYear });
          return;
        }


        if (!eventsData?.eventsData?.events) {
          console.log('Missing required data for processing')
          setProcessedData([])
          return
        }

        const rawEvents = externalPerformanceData?.leadership?.events || 
                     eventsData?.eventsData?.events || [];

        setProcessing(true)
        console.log('=== DEBUG: Processing Real Data ===')
        console.log('📅 Current Fiscal Year:', getFiscalYearLabel(fiscalYear));
        console.log('Competencies available:', competencies.length)
        
         const events = (eventsData.eventsData.events || []).filter(event => {
          // Filter by org unit
          const scopeMatch = filterScope === 'all' ||
            accessibleOrgUnitIds.length === 0 ||
            accessibleOrgUnitIds.includes(event.orgUnit);
          
          if (!scopeMatch) return false;
          
          // ✅ Filter by fiscal year using the helper
          const eventDate = event.eventDate || event.created;
          const fiscalYearMatch = isWithinFiscalYear(eventDate, fiscalYear);
          
          if (!fiscalYearMatch && eventDate) {
            console.log(`⏭️ Skipping event from ${eventDate} - not in current fiscal year`);
          }
          
          return fiscalYearMatch;
        })
        
        console.log(`✅ ${events.length} events found in current fiscal year (${getFiscalYearLabel(fiscalYear)})`)
        
        setRawEvents(events)

        // Fetch all users for mapping
        const userMap = await fetchAllUsers()
        console.log(`Loaded ${userMap.size} users for staff mapping`)

        const staffEventsMap = new Map()
        
        events.forEach(event => {
          const staffId = event.trackedEntityInstance || event.orgUnit
          if (!staffEventsMap.has(staffId)) {
            staffEventsMap.set(staffId, [])
          }
          staffEventsMap.get(staffId).push(event)
        })
        
        console.log(`Found ${staffEventsMap.size} unique staff members`)
        
        const staffEntries = []
        
        for (const [staffId, staffEvents] of staffEventsMap.entries()) {
          const staffRatings = {}
          let latestEventDate = null
          let latestEvent = null
          let staffName = null
          let position = null
          let userId = null

          // FIRST: Try to get staff name from user account using staffId as username
          if (staffId && !staffName && userMap.has(staffId)) {
            const user = userMap.get(staffId)
            staffName = user.name
            userId = user.id
            console.log(`Found staff name from username mapping: ${staffName} (username: ${staffId}, userId: ${userId})`)
          }

          // SECOND: Fetch TEI details to get username and user ID
          if (staffId && staffId !== 'undefined' && staffEvents.length > 0) {
            try {
              console.log(`🔍 Fetching TEI for staffId: ${staffId}`)
              
              const teiQuery = {
                tei: {
                  resource: `trackedEntityInstances/${staffId}`,
                  params: {
                    fields: 'trackedEntityInstance,attributes[attribute,value],orgUnit'
                  }
                }
              }
              const teiResult = await engine.query(teiQuery)
              const tei = teiResult.tei

              console.log(`📋 Staff TEI Response for ${staffId}:`, JSON.stringify(tei, null, 2))

              // Check staff TEI for username or user ID
              if (tei && tei.attributes) {
                for (const attr of tei.attributes) {
                  const value = attr.value
                  
                  // Check if attribute value is a username
                  if (value && userMap.has(value)) {
                    const user = userMap.get(value)
                    staffName = user.name
                    userId = user.id
                    console.log(`Found staff name from username attribute: ${staffName}, userId: ${userId}`)
                  }
                  
                  // Check if value is a user ID
                  if (value && value.length === 11 && /^[A-Za-z0-9]+$/.test(value) && userMap.has(value)) {
                    const user = userMap.get(value)
                    staffName = user.name
                    userId = value
                    console.log(`Found staff name from user ID attribute: ${staffName}`)
                  }
                }
              }
            } catch (error) {
              console.log(`❌ Could not fetch TEI details for ${staffId}:`, error)
            }
          }
          
        
          if (userId && !position) {
      try {
        console.log(`🔍 Looking for position with user ID: ${userId}`);
        
        // Use the correct attribute ID to filter
        const positionQuery = {
          positionTei: {
            resource: 'trackedEntityInstances',
            params: {
              trackedEntityType: 'UGGo0Ebjl0E',
              ou: orgUnitId,
              ouMode: 'DESCENDANTS',
              filter: `${POSITION_ATTRIBUTES.USER_ID}:eq:${userId}`,
              fields: `trackedEntityInstance,attributes[attribute,value],orgUnit`,
              pageSize: 10
            }
          }
        };
        
        const positionResult = await engine.query(positionQuery);
        const positions = positionResult.positionTei?.trackedEntityInstances || [];
        
        if (positions.length > 0) {
          const positionTei = positions[0];
          console.log(`Found position TEI for user ${userId}:`, positionTei.trackedEntityInstance);
          
          // Extract the position title using the correct attribute ID
          if (positionTei.attributes) {
            const titleAttr = positionTei.attributes.find(attr => attr.attribute === POSITION_ATTRIBUTES.TITLE);
            if (titleAttr && titleAttr.value) {
              position = titleAttr.value;
              console.log(`✅ Found position for ${staffName}: ${position}`);
            }
          }
        } else {
          console.log(`No position TEI found for user ${userId}`);
        }
      } catch (error) {
        console.log(`Error finding position for user ${userId}:`, error);
      }
    }
      

      // FOURTH: Try to get from event data values
      for (const event of staffEvents) {
        if (event?.dataValues) {
          if (!staffName) {
            const nameDV = event.dataValues.find(dv => 
              dv.dataElement === 'staffName' ||
              dv.dataElement === 'employeeName' ||
              dv.dataElement === 'fullName' ||
              dv.dataElement.includes('name')
            )
            if (nameDV && nameDV.value && nameDV.value !== 'Unknown' && nameDV.value !== 'Staff Member') {
              staffName = nameDV.value
              console.log(`Found staff name in event: ${staffName}`)
            }
          }
          
          if (!position) {
            const positionDV = event.dataValues.find(dv => 
              dv.dataElement === 'position' || 
              dv.dataElement === 'jobTitle' ||
              dv.dataElement.includes('position')
            )
            if (positionDV && positionDV.value && positionDV.value !== 'Staff Member') {
              position = positionDV.value
              console.log(`Found position in event: ${position}`)
            }
          }
          
          // Also check for user ID in events
          if (!staffName) {
            const userIDV = event.dataValues.find(dv => 
              dv.dataElement === 'userId' ||
              dv.dataElement === 'assignedUser' ||
              dv.dataElement === 'user'
            )
            if (userIDV && userIDV.value && userMap.has(userIDV.value)) {
              const user = userMap.get(userIDV.value)
              staffName = user.name
              userId = user.id
              console.log(`Found staff name from event user ID: ${staffName}, userId: ${userId}`)
            }
          }
        }
      }

      // FIFTH: Try to get from orgUnit name as last resort
      if (!staffName && staffEvents[0]?.orgUnitName) {
        const orgUnitName = staffEvents[0].orgUnitName
        if (orgUnitName && orgUnitName !== 'Unknown' && !orgUnitName.includes('Department') && !orgUnitName.includes('Office')) {
          staffName = orgUnitName
          console.log(`Using orgUnit name as staff name: ${staffName}`)
        }
      }

      // Set fallback values
      if (!staffName) {
        staffName = `Staff (${staffId.substring(0, 8)})`
      }
      if (!position) {
        position = 'Staff'
      }

      console.log(`Final - Staff: ${staffName}, Position: ${position}, ID: ${staffId}, UserID: ${userId}`)

      // Process ratings for this staff member
      for (const event of staffEvents) {
        event.dataValues?.forEach(dv => {
          const num = parseInt(dv.value)
          if (!isNaN(num) && num >= 1 && num <= 5) {
            let competencyId = null
            
            for (const comp of competencies) {
              if (dv.dataElement === comp.competencyId || 
                  dv.dataElement.includes(comp.name) ||
                  dv.dataElement.toLowerCase().includes(comp.name.toLowerCase())) {
                competencyId = comp.competencyId
                break
              }
            }
            
            if (!competencyId && competencies.length > 0) {
              competencyId = competencies[0].competencyId
            }
            
            if (competencyId) {
              if (!staffRatings[competencyId]) {
                staffRatings[competencyId] = []
              }
              staffRatings[competencyId].push({
                value: num,
                date: event.eventDate,
                eventId: event.event
              })
            }
          }
        })
        
        if (!latestEventDate || (event.eventDate && event.eventDate > latestEventDate)) {
          latestEventDate = event.eventDate
          latestEvent = event
        }
      }
      
      // Calculate scores
      const rawScores = {}
      for (const [compId, ratings] of Object.entries(staffRatings)) {
        const sortedRatings = ratings.sort((a, b) => new Date(b.date) - new Date(a.date))
        rawScores[compId] = sortedRatings[0]?.value || 0
      }
      
      for (const comp of competencies) {
        if (!rawScores[comp.competencyId]) {
          rawScores[comp.competencyId] = 0
        }
      }
      
      staffEntries.push({
        id: staffId,
        name: staffName,
        position: position,
        rawScores: rawScores,
        reviews: staffEvents.map(event => ({
          id: event.event,
          reviewType: event?.programStage === CONFIG.stages.midYear ? 'Mid-Year' : 
                      event?.programStage === CONFIG.stages.endYear ? 'Year-End' : 'Unknown',
          date: event?.eventDate || new Date().toISOString(),
          orgUnit: event?.orgUnitName || 'Unknown',
          rawScores: rawScores
        })),
        latestReview: {
          id: latestEvent?.event,
          reviewType: latestEvent?.programStage === CONFIG.stages.midYear ? 'Mid-Year' : 
                      latestEvent?.programStage === CONFIG.stages.endYear ? 'Year-End' : 'Unknown',
          date: latestEventDate || new Date().toISOString(),
          orgUnit: latestEvent?.orgUnitName || 'Unknown',
          rawScores: rawScores
        }
      })
    }
      
    console.log(`Processed ${staffEntries.length} staff members`)
    
    // Calculate final scores
    const result = staffEntries.map(staff => {
      const latest = staff.latestReview
      const scores = {}
      let totalScore = 0

      competencies.forEach(comp => {
        const compId = comp.competencyId
        const rawScore = latest.rawScores[compId] || 0
        const weightedScore = rawScore * comp.weight
        
        scores[compId] = {
          name: comp.name,
          raw: rawScore,
          weighted: parseFloat(weightedScore.toFixed(2))
        }
        
        totalScore += weightedScore
      })

      return {
        ...staff,
        totalScore: parseFloat(totalScore.toFixed(2)),
        lastReview: latest.date,
        reviewType: latest.reviewType,
        scores: scores,
      }
    })

    console.log('Final processed data:', result)
    setProcessedData(result)
    setProcessing(false)
  }

  processData()
}, [eventsData, competencies, eventsLoading, loadingCompetencies, accessibleOrgUnitIds, filterScope, fiscalYear])

  // Loading state
  if (eventsLoading || loadingCompetencies || processing || !fiscalYear?.start || !fiscalYear?.end) {
    return (
      <Card style={{ padding: 16, paddingLeft: '6px' }}>
        <h3>Leadership & Management Dashboard</h3>
        <LinearLoader amount={50} />
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          {!fiscalYear?.start || !fiscalYear?.end 
            ? 'Loading fiscal year configuration...' 
            : 'Loading leadership performance data...'}
        </div>
      </Card>
    )
  }

  const hasRealData = processedData.length > 0

  // If no data is available, show empty state with debug info
if (!hasRealData) {
  const events = (eventsData?.eventsData?.events || []).filter(event => {
    const scopeMatch = filterScope === 'all' ||
      accessibleOrgUnitIds.length === 0 ||
      accessibleOrgUnitIds.includes(event.orgUnit);
    
    if (!scopeMatch) return false;
    
    const eventDate = event.eventDate || event.created;
    return isWithinFiscalYear(eventDate, fiscalYear);
  });
  
  const midYearEvents = events.filter(e => e.programStage === CONFIG.stages.midYear).length
  const endYearEvents = events.filter(e => e.programStage === CONFIG.stages.endYear).length
  const weightEvents = events.filter(e => e.programStage === CONFIG.stages.weights).length
  
  const ratingDataElements = new Set()
  events.forEach(event => {
    event.dataValues?.forEach(dv => {
      const num = parseInt(dv.value)
      if (!isNaN(num) && num >= 1 && num <= 5) {
        ratingDataElements.add(dv.dataElement)
      }
    })
  })

  return (
    <Card style={{ padding: isMobile ? 12 : 16, marginBottom: 24 }}>
      <AlertBar warning>
        {competencies.length > 0 
          ? `Found ${events.length} leadership events but no rating values detected.`
          : 'No competencies configured. Please set up competencies first.'}
      </AlertBar>

      <Card style={{ padding: 40, textAlign: 'center', marginTop: 24 }}>
        <div style={{ fontSize: 48, color: '#e9ecef', marginBottom: 16 }}>📊</div>
        <h4 style={{ color: '#6c757d', marginBottom: 8 }}>Leadership Data Debug Info</h4>
        <p style={{ color: '#6c757d', marginBottom: 16, textAlign: 'left' }}>
          <strong>Events Found:</strong> {events.length}<br/>
          <strong>Mid-Year Reviews:</strong> {midYearEvents}<br/>
          <strong>End-Year Reviews:</strong> {endYearEvents}<br/>
          <strong>Weight Configurations:</strong> {weightEvents}<br/>
          <strong>Competencies Configured:</strong> {competencies.length}<br/>
          <strong>Rating Data Elements Found:</strong> {Array.from(ratingDataElements).join(', ') || 'None'}<br/>
        </p>
        <div style={{ fontSize: 14, color: '#6c757d' }}>
          Expected rating data element: <code>{CONFIG.de.ratingScore}</code><br/>
          If you're seeing different data elements, the leadership data might be stored differently.
        </div>
      </Card>
    </Card>
  )
}
  const aggregateScores = calculateAggregateScores(processedData, competencies)
  const averageWeights = calculateAverageWeights(processedData, competencies)

  const radarData = competencies.map(comp => {
    const compId = comp.competencyId
    const avgScore = parseFloat(aggregateScores[compId]?.average || 0)
    return {
      subject: comp.name.length > 15 ? comp.name.substring(0, 15) + '...' : comp.name,
      fullSubject: comp.name,
      score: (avgScore / 5) * 100,
      fullMark: 100,
      weight: averageWeights[compId] ? (averageWeights[compId] * 100).toFixed(0) : 'N/A',
      averageScore: avgScore.toFixed(2)
    }
  })

  // If staff is selected, show individual view
  if (selectedStaff && viewMode === 'individual') {
    return (
      <IndividualLeadershipView
        staff={selectedStaff}
        competencies={competencies}
        competencyDefinitions={competencyDefinitions}
        onBack={() => {
          setSelectedStaff(null)
          setViewMode('aggregate')
        }}
      />
    )
  }

  return (
    <Card style={{ padding: isMobile ? 12 : 16, marginBottom: 24 }}>
      {/* Data Quality Warning */}
      {processedData.some(staff => {
        const hasMissingData = competencies.some(comp => 
          !staff.scores[comp.competencyId] || staff.scores[comp.competencyId].raw === 0
        )
        return hasMissingData
      }) && (
        <AlertBar warning style={{ marginBottom: 24 }}>
          ⚠️ Some staff members have incomplete competency data. 
          Only {competencies.filter(comp => 
            processedData.some(staff => staff.scores[comp.competencyId]?.raw > 0)
          ).length} out of {competencies.length} competencies have data.
        </AlertBar>
      )}
      
      {/* Overall Score Card */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '220px' : '300px'}, 1fr))`, gap: 16, margin: 20 }}>
        <Card style={{
          padding: 20,
          backgroundColor: '#007bff',
          color: 'white',
          textAlign: 'center', paddingLeft: '6px'
        }}>
          <div style={{ fontSize: 20, opacity: 0.9, paddingLeft: '6px' }}>Company Average Score</div>
          <div style={{ fontSize: 38, fontWeight: 'bold', margin: '8px 0', paddingLeft: '6px' }}>
            {aggregateScores.overall.average}
          </div>
          <div style={{ fontSize: 14, opacity: 0.9, padding: '6px' }}>
            Average total score across {processedData.length} staff members (Max: 5.00)
          </div>
        </Card>
      </div>

      {/* Radar Chart and Score Breakdown */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: 24,
        marginBottom: 24
      }}>
        {/* Radar Chart */}
        {competencies.length > 0 && (
          <div>
            <h4 style={{ marginBottom: 16, paddingLeft: '6px' }}>Overall Company Competency Averages</h4>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" />
                  <PolarRadiusAxis domain={[0, 100]} />
                  <Radar
                    name="Average Score (%)"
                    dataKey="score"
                    stroke="#8884d8"
                    fill="#8884d8"
                    fillOpacity={0.6}
                  />
                  <Tooltip
                    formatter={(value, name, props) => {
                      if (name === 'score') {
                        return [
                          `${value}% (Avg: ${props.payload.averageScore})`,
                          'Score'
                        ]
                      }
                      return value
                    }}
                    labelFormatter={(value, payload) => {
                      if (payload && payload[0]) {
                        return `${payload[0].payload.fullSubject} (Weight: ${payload[0].payload.weight}%)`
                      }
                      return value
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Score Breakdown */}
        <div>
          <h4 style={{ marginBottom: 16, paddingLeft: '6px' }}>Overall Company Average Scores</h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: 12,
            marginBottom: 16
          }}>
            {competencies.map((comp, index) => {
              const compId = comp.competencyId
              const colors = ['#e83e8c', '#ffc107', '#20c997', '#17a2b8', '#6f42c1', '#fd7e14']
              const color = colors[index % colors.length]
              
              return (
                 <div style={{ display: 'grid', gap: 6, margin: 10, borderRadius: '4px', boxShadow: '0 4px 8px rgb(173, 169, 169)',}}>
                  <Card key={compId} style={{ padding: 12, borderLeft: `4px solid ${color}`,  }}>
                    <div style={{ fontWeight: '600', color: color, fontSize: 14, paddingLeft: '6px' }}>
                      {comp.name.length > 12 ? comp.name.substring(0, 12) + '...' : comp.name}
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 'bold', margin: '8px 0', paddingLeft: '6px' }}>
                      {aggregateScores[compId]?.average || '0.00'}
                    </div>
                    <div style={{ fontSize: 12, color: '#6c757d', padding: '6px' }}>
                      Weight: {averageWeights[compId] ? (averageWeights[compId] * 100).toFixed(0) + '%' : 'N/A'}
                    </div>
                  </Card>
                </div>
              )
            })}
          </div>
        </div>
      </div>

       {/* Staff Performance Table */}
        <div style={{ marginTop: 24 }}>
          <h4 style={{ marginBottom: 16, paddingLeft: isMobile ? 0 : '6px' }}>
            Staff Performance Details
          </h4>

          {isMobile ? (
            /* ===== MOBILE: Card layout ===== */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {processedData.map((staff) => {
                const colors = ['#e83e8c', '#ffc107', '#20c997', '#17a2b8', '#6f42c1', '#fd7e14'];
                return (
                  <Card key={staff.id} style={{ padding: 16 }}>
                    {/* Header: name + position + total */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 12,
                      marginBottom: 12,
                    }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {staff.name}
                        </div>
                        <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>
                          {staff.position}
                        </div>
                        <div style={{ fontSize: 11, color: '#6c757d', marginTop: 4 }}>
                          {staff.lastReview ? new Date(staff.lastReview).toLocaleDateString() : 'N/A'} • {staff.reviewType}
                        </div>
                      </div>
                      <div style={{
                        fontSize: 22,
                        fontWeight: 'bold',
                        color: staff.totalScore >= 4 ? '#28a745' : staff.totalScore >= 3 ? '#ffc107' : '#dc3545',
                        whiteSpace: 'nowrap',
                      }}>
                        {staff.totalScore.toFixed(2)}
                      </div>
                    </div>

                    {/* Competency scores */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                      gap: 8,
                      marginBottom: 12,
                    }}>
                      {competencies.map((comp, index) => {
                        const compId = comp.competencyId;
                        const score = staff.scores[compId] || { raw: 0, weighted: 0 };
                        const color = colors[index % colors.length];
                        const hasData = score.raw > 0;
                        return (
                          <div key={compId} style={{
                            padding: 8,
                            border: '1px solid #e9ecef',
                            borderRadius: 6,
                            backgroundColor: '#fafbfc',
                          }}>
                            <div style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: color,
                              marginBottom: 4,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }} title={comp.name}>
                              {comp.name}
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
                              {score.weighted.toFixed(2)}
                            </div>
                            <ProgressBar value={(score.raw / 5) * 100} height="4px" color={hasData ? color : '#e9ecef'} />
                            <div style={{ fontSize: 10, color: '#6c757d', marginTop: 2 }}>
                              Raw: {score.raw}/5
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => { setSelectedStaff(staff); setViewMode('individual'); }}
                      style={{
                        width: '100%',
                        backgroundColor: 'rgb(60, 57, 250)',
                        color: '#fff',
                        borderRadius: 8,
                        cursor: 'pointer',
                        border: 'none',
                        height: 36,
                        fontWeight: 600,
                      }}
                    >
                      View Details
                    </button>
                  </Card>
                );
              })}
            </div>
          ) : (
            /* ===== DESKTOP: existing grid layout ===== */
            <Card style={{ padding: 16, overflowX: 'auto' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: `minmax(150px, 1fr) minmax(100px, 0.5fr) minmax(100px, 0.5fr) minmax(100px, 0.5fr) ${competencies.map(() => 'minmax(100px, 0.8fr)').join(' ')} minmax(80px, 0.5fr) minmax(100px, 0.5fr)`,
                gap: '12px',
                padding: '12px 16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '12px',
                fontWeight: '600',
                fontSize: '14px',
                minWidth: `${800 + (competencies.length * 100)}px`,
              }}>
                <div>Staff Name</div>
                <div>Position</div>
                <div>Last Review</div>
                <div>Review Type</div>
                {competencies.map(comp => (
                  <div key={comp.competencyId} title={comp.definition || comp.name}>
                    {comp.name.length > 10 ? comp.name.substring(0, 10) + '...' : comp.name}
                  </div>
                ))}
                <div>Total Score</div>
                <div>Actions</div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {processedData.map((staff) => {
                  const colors = ['#e83e8c', '#ffc107', '#20c997', '#17a2b8', '#6f42c1', '#fd7e14'];
                  return (
                    <div key={staff.id} style={{
                      display: 'grid',
                      gridTemplateColumns: `minmax(150px, 1fr) minmax(100px, 0.5fr) minmax(100px, 0.5fr) minmax(100px, 0.5fr) ${competencies.map(() => 'minmax(100px, 0.8fr)').join(' ')} minmax(80px, 0.5fr) minmax(100px, 0.5fr)`,
                      gap: '12px',
                      padding: '16px',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      alignItems: 'center',
                      minWidth: `${800 + (competencies.length * 100)}px`,
                    }}>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>{staff.name}</div>
                      <div style={{ fontSize: '14px', color: '#6c757d' }}>{staff.position}</div>
                      <div style={{ fontSize: '14px', color: '#6c757d' }}>
                        {staff.lastReview ? new Date(staff.lastReview).toLocaleDateString() : 'N/A'}
                      </div>
                      <div>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          backgroundColor: staff.reviewType === 'Mid-Year' ? '#fff3cd' : '#d1ecf1',
                          color: staff.reviewType === 'Mid-Year' ? '#856404' : '#0c5460',
                          whiteSpace: 'nowrap',
                        }}>
                          {staff.reviewType || 'Unknown'}
                        </span>
                      </div>

                      {competencies.map((comp, index) => {
                        const compId = comp.competencyId;
                        const score = staff.scores[compId] || { raw: 0, weighted: 0 };
                        const color = colors[index % colors.length];
                        const hasData = score.raw > 0;
                        return (
                          <div key={compId} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {score.weighted.toFixed(2)}
                              {!hasData && (
                                <span style={{
                                  fontSize: '10px',
                                  backgroundColor: '#ffc107',
                                  color: '#856404',
                                  padding: '2px 4px',
                                  borderRadius: '4px',
                                }}>
                                  Missing
                                </span>
                              )}
                            </div>
                            <ProgressBar value={(score.raw / 5) * 100} height="4px" color={hasData ? color : '#e9ecef'} />
                            <div style={{ fontSize: '11px', color: '#6c757d' }}>Raw: {score.raw}/5</div>
                          </div>
                        );
                      })}

                      <div style={{
                        fontWeight: 'bold',
                        fontSize: '16px',
                        color: staff.totalScore >= 4 ? '#28a745' : staff.totalScore >= 3 ? '#ffc107' : '#dc3545',
                        textAlign: 'center',
                      }}>
                        {staff.totalScore.toFixed(2)}
                      </div>

                      <div>
                        <button
                          onClick={() => { setSelectedStaff(staff); setViewMode('individual'); }}
                          style={{
                            width: '100%',
                            backgroundColor: 'rgb(60, 57, 250)',
                            color: '#fff',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            border: 'none',
                            boxShadow: '0 8px 10px rgba(40, 18, 18, 0.2)',
                            height: '35px',
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
    </Card>
  )
}

// Helper functions
const calculateAggregateScores = (staffList, competencies) => {
  const staffCount = staffList.length
  if (staffCount === 0) {
    return { overall: { total: 0, average: 0 } }
  }

  const aggregate = { overall: { total: 0, average: 0 } }
  competencies.forEach(comp => {
    aggregate[comp.competencyId] = { total: 0, average: 0, name: comp.name }
  })

  staffList.forEach(staff => {
    aggregate.overall.total += staff.totalScore
    competencies.forEach(comp => {
      const compId = comp.competencyId
      if (staff.scores[compId]) {
        aggregate[compId].total += staff.scores[compId].weighted
      }
    })
  })

  aggregate.overall.average = (aggregate.overall.total / staffCount).toFixed(2)
  competencies.forEach(comp => {
    const compId = comp.competencyId
    if (aggregate[compId]) {
      aggregate[compId].average = (aggregate[compId].total / staffCount).toFixed(2)
    }
  })

  return aggregate
}

const calculateAverageWeights = (staffList, competencies) => {
  if (staffList.length === 0) {
    const defaultWeight = competencies.length > 0 ? (1 / competencies.length) : 0.25
    const defaultWeights = {}
    competencies.forEach(comp => {
      defaultWeights[comp.competencyId] = defaultWeight
    })
    return defaultWeights
  }

  const averageWeights = {}
  competencies.forEach(comp => {
    averageWeights[comp.competencyId] = comp.weight || (1 / competencies.length)
  })
  return averageWeights
}

// IndividualLeadershipView component
const IndividualLeadershipView = ({ staff, competencies, competencyDefinitions, onBack }) => {
  const ProgressBar = ({ value, height = '6px', borderRadius = '3px', color = '#007bff' }) => (
    <div style={{
      width: '100%',
      height,
      backgroundColor: '#e5e7eb',
      borderRadius
    }}>
      <div style={{
        width: `${Math.min(Math.max(value || 0, 0), 100)}%`,
        height: '100%',
        backgroundColor: color,
        borderRadius,
        transition: 'width 0.3s ease'
      }} />
    </div>
  )

  const radarData = competencies.map((comp, index) => {
    const compId = comp.competencyId
    const score = staff.scores[compId] || { raw: 0, weighted: 0 }
    const colors = ['#e83e8c', '#ffc107', '#20c997', '#17a2b8', '#6f42c1', '#fd7e14']
    
    return {
      subject: comp.name.length > 15 ? comp.name.substring(0, 15) + '...' : comp.name,
      fullSubject: comp.name,
      score: (score.raw / 5) * 100,
      fullMark: 100,
      weight: comp.weight ? (comp.weight * 100).toFixed(0) : 'N/A',
      rawScore: score.raw,
      weightedScore: score.weighted,
      color: colors[index % colors.length],
      definition: competencyDefinitions[comp.name] || comp.definition || 'No definition available'
    }
  })

  return (
    <Card style={{ padding: 16, marginBottom: 24, paddingLeft: '6px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <Button primary small onClick={onBack}>
            ← Back to Overall Company View
          </Button>
          <h3 style={{ margin: 0 }}>{staff.name} - Leadership Performance</h3>
        </div>
        <div style={{ fontSize: 14, color: '#6c757d' }}>
          {staff.position} • Last Review: {staff.lastReview ? new Date(staff.lastReview).toLocaleDateString() : 'N/A'} • {staff.reviewType}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
        <Card style={{ padding: 20, backgroundColor: '#28a745', color: 'white', textAlign: 'center' }}>
          <div style={{ fontSize: 14, opacity: 0.9 }}>Individual Total Score</div>
          <div style={{ fontSize: 48, fontWeight: 'bold', margin: '8px 0' }}>{staff.totalScore.toFixed(2)}</div>
          <div style={{ fontSize: 14, opacity: 0.9 }}>Based on weighted competency scores (Max: 5.00)</div>
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 24, marginBottom: 24 }}>
        {competencies.length > 0 && (
          <div>
            <h4 style={{ marginBottom: 16 }}>Competency Scores</h4>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" />
                  <PolarRadiusAxis domain={[0, 100]} />
                  <Radar name="Score (%)" dataKey="score" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                  <Tooltip formatter={(value, name, props) => {
                    if (name === 'score') return [`${value}% (${props.payload.rawScore}/5)`, 'Score']
                    return value
                  }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div>
          <h4 style={{ marginBottom: 16 }}>Detailed Score Breakdown</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
            {competencies.map((comp, index) => {
              const compId = comp.competencyId
              const score = staff.scores[compId] || { raw: 0, weighted: 0 }
              const colors = ['#e83e8c', '#ffc107', '#20c997', '#17a2b8', '#6f42c1', '#fd7e14']
              const color = colors[index % colors.length]
              
              return (
                <Card key={compId} style={{ padding: 12, borderLeft: `4px solid ${color}`, paddingLeft: '6px' }}>
                  <div style={{ fontWeight: '600', color: color, fontSize: 14, paddingLeft: '6px'  }}>{comp.name}</div>
                  <div style={{ fontSize: 24, fontWeight: 'bold', margin: '8px 0', paddingLeft: '6px'  }}>{score.weighted.toFixed(2)}</div>
                  <div style={{ fontSize: 12, color: '#6c757d', paddingLeft: '6px'  }}>Raw: {score.raw}/5 • Weight: {comp.weight ? (comp.weight * 100).toFixed(0) + '%' : 'N/A'}</div>
                  <div style={{ fontSize: 11, color: '#6c757d', marginTop: 4, paddingLeft: '6px'  }}>
                    Score: {score.raw}/5 • {((score.raw / 5) * 100).toFixed(0)}% of target
                    {score.raw === 0 && <span style={{ marginLeft: '4px', color: '#ff9800', paddingLeft: '6px'  }}>⚠️ No data</span>}
                  </div>
                  {competencyDefinitions[comp.name] && (
                    <div style={{ fontSize: 11, color: '#007bff', marginTop: 8, fontStyle: 'italic', paddingLeft: '6px' }}>
                      💡 {competencyDefinitions[comp.name]}
                    </div>
                  )}
                </Card>
              )
            })}
          </div>
        </div>
      </div>
    </Card>
  )
}

export default LeadershipDashboard